import {SourcingAvailabilityCheckApiResponse} from '@dms/api/core';

import {
  AVAILABILITY_CHECK_MAX_AD_IDS,
  AVAILABILITY_CHECK_POLLING_INTERVAL_MS,
} from '../../../constants/sourcingAvailabilityCheck';
import {UnconfirmedAvailabilityError} from '../constants/UnconfirmedAvailabilityError';

const MAX_UNCONFIRMED_ATTEMPTS = 3;

function findUnconfirmedIds(
  adIds: string[],
  statuses: Map<string, SourcingAvailabilityCheckApiResponse['results'][number]['status']>,
  soldIds: Set<string>
) {
  const retryIds: string[] = [];
  for (const adId of adIds) {
    const status = statuses.get(adId);
    if (status === 'just_sold') {
      soldIds.add(adId);
    } else if (status !== 'active' && status !== 'unsupported_platform') {
      // Unsupported platforms are eligible. Unknown or missing results need rechecking.
      retryIds.push(adId);
    }
  }
  return retryIds;
}

/** Recheck the whole cart, including pages that were never visible in the table. */
export async function filterOutSoldAdIds(
  adIds: string[],
  checkAvailability: (adIds: string[]) => Promise<SourcingAvailabilityCheckApiResponse>,
  assertContext: () => void
) {
  const soldIds = new Set<string>();
  for (let index = 0; index < adIds.length; index += AVAILABILITY_CHECK_MAX_AD_IDS) {
    let unconfirmedIds = adIds.slice(index, index + AVAILABILITY_CHECK_MAX_AD_IDS);
    for (let attempt = 0; attempt < MAX_UNCONFIRMED_ATTEMPTS; attempt += 1) {
      if (attempt > 0) {
        await new Promise<void>((resolve) =>
          setTimeout(resolve, AVAILABILITY_CHECK_POLLING_INTERVAL_MS)
        );
      }
      assertContext();
      const result = await checkAvailability(unconfirmedIds);
      assertContext();
      const statuses = new Map(result.results.map(({adId, status}) => [adId, status]));
      unconfirmedIds = findUnconfirmedIds(unconfirmedIds, statuses, soldIds);
      if (unconfirmedIds.length === 0) {
        break;
      }
    }
    if (unconfirmedIds.length > 0) {
      throw new UnconfirmedAvailabilityError(unconfirmedIds);
    }
  }
  return adIds.filter((id) => !soldIds.has(id));
}
